import { useEffect, type ReactNode } from 'react';
import { SCREENS, type BackTarget, type ScreenId } from '../app/registry';
import { useGo } from '../app/navigation';
import { useFeatureIsOn } from '../lib/flags';
import { useT } from '../i18n';
import { openConsentSettings } from '../lib/analytics';
import { Header } from './Header';

export interface ScreenProps {
  id: ScreenId;
  /**
   * Back arrow: a screen ID, 'home', a handler, or null to hide it. Defaults to the
   * registry's `back` (e.g. S13 passes null on the first pass, S12 on a return).
   */
  back?: BackTarget | (() => void);
  /** Show the rail. Defaults to on when the screen has a stage (and the flag is on). */
  rail?: boolean;
  /** Hide the whole header (back + logo + rail). Nothing uses it yet. */
  chrome?: boolean;
  /** The pinned bottom dock: the primary action and at most one way out. */
  dock?: ReactNode;
  /** Document title (defaults to the brand title). */
  title?: string;
  children: ReactNode;
}

/**
 * Every screen renders exactly one <Screen>. It draws the chrome from the registry,
 * the scrollable pane and the dock. Scroll-to-top and the keyboard lift are the shell's.
 */
export function Screen({ id, back, rail, chrome = true, dock, title, children }: ScreenProps) {
  const def = SCREENS[id];
  const go = useGo();
  const t = useT();
  const railFlag = useFeatureIsOn('progress_rail');
  const target = back === undefined ? def.back : back;
  const onBack = typeof target === 'function' ? target : target === null ? undefined : () => go(target);
  const showRail = (rail ?? true) && railFlag;

  useEffect(() => {
    document.title = title ? `${title} · DelayBuddy` : t('common.documentTitle');
  }, [title, t]);

  return (
    <div className="screen" data-screen={id}>
      {chrome && <Header onBack={onBack} stage={showRail ? def.stage : null} />}
      <main className={`pane${dock ? '' : ' pane--nodock'}`}>{children}</main>
      {dock && (
        <div className="dock" data-testid="dock">
          {dock}
        </div>
      )}
      {/* The quiet way back to the cookie banner (w5-08), at the foot of every screen. After the
          dock in the DOM, so Tab reaches the screen's action first (w6-04 N6); the pinned dock
          still sits over it on screen, as before, and in landscape (dock in flow) it follows it. */}
      <footer className={`pane__foot${dock ? '' : ' pane__foot--nodock'}`}>
        <button type="button" className="pane__cookies" onClick={openConsentSettings}>
          {t('common.cookieSettings')}
        </button>
      </footer>
    </div>
  );
}
